import React, { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import SetlistEditor from './SetlistEditor';
import SongPicker from './SongPicker';
import { ITEM_TYPES } from './setlistItemTypes';
import { addSetlistItem as addSetlistItemRequest, removeSetlistItem as removeSetlistItemRequest } from './setlistApi';

const EMPTY_FORM = { title: '', item_type: 'lied', song_id: '', key: '', bpm: '', duration_minutes: '', note: '', pdf_link_url: '', pdf_link_label: '' };

/**
 * Dienstvolgorde van één dienst: sleepbare lijst, liedzoeker en het formulier voor overige onderdelen.
 * Toevoegen en verwijderen werken per onderdeel (POST/DELETE): bestaande onderdelen houden hun id, instellingen en volgorde.
 */
const SetlistPanel = ({ service, canManage, songs, onServiceChange, onOpenItem }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [mutating, setMutating] = useState(false);
  const artistBySongId = useMemo(() => Object.fromEntries(songs.filter((song) => song.artist).map((song) => [song.id, song.artist])), [songs]);

  const mutate = async (request, failureMessage) => {
    setMutating(true);
    try {
      const updated = await request();
      onServiceChange(updated);
      return updated;
    } catch (error) {
      toast.error(error.response?.data?.detail || failureMessage);
      throw error;
    } finally {
      setMutating(false);
    }
  };

  // Overige onderdelen (gebed, mededeling, ...) en vrije titels; liederen uit de bibliotheek gaan via addSong.
  const addOther = async (event) => {
    event.preventDefault();
    const item = {
      item_type: form.item_type,
      title: form.title,
      song_id: null,
      key: form.key || null,
      bpm: null,
      duration_minutes: form.duration_minutes ? Number(form.duration_minutes) : null,
      note: form.note || null,
      pdf_link_url: form.pdf_link_url || null,
      pdf_link_label: form.pdf_link_label || null,
    };
    if (!item.title) {
      toast.error('Vul een titel in');
      return;
    }
    try {
      await mutate(() => addSetlistItemRequest(service.id, item), 'Setlist bijwerken mislukt');
      setForm(EMPTY_FORM);
      toast.success('Setlist bijgewerkt');
    } catch (error) { /* melding al getoond */ }
  };

  // Een lied uit de bibliotheek: de bestaande song-id, met dezelfde standaardwaarden als voorheen (toonsoort, tempo, PDF-link).
  const addSong = (song) => mutate(() => addSetlistItemRequest(service.id, {
    item_type: 'lied',
    title: song.title,
    song_id: song.id,
    key: song.default_key || null,
    bpm: song.bpm || null,
    pdf_link_url: song.pdf_link_url || null,
    pdf_link_label: song.pdf_link_label || null,
  }), 'Lied toevoegen mislukt');

  const remove = (item) => mutate(() => removeSetlistItemRequest(service.id, item.id), 'Onderdeel verwijderen mislukt').catch(() => {});

  return <>
    <SetlistEditor service={service} canManage={canManage} busy={mutating} onServiceChange={onServiceChange} onOpenItem={onOpenItem} onRemoveItem={remove}
      artistBySongId={artistBySongId} emptyText={canManage ? 'Nog geen onderdelen toegevoegd. Zoek hieronder een lied of voeg een ander onderdeel toe.' : 'Deze setlist is nog niet beschikbaar.'}
      getItemMeta={(item) => `${ITEM_TYPES.find(([value]) => value === item.item_type)?.[1] || ''}${item.key ? ` · ${item.key}` : ''}${item.duration_minutes ? ` · ${item.duration_minutes} min` : ''}`} />
    {canManage && <div className="mt-4 space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3"><h4 className="text-sm font-semibold text-slate-900">Lied toevoegen uit de bibliotheek</h4><SongPicker items={service.setlist || []} onAdd={addSong} /></div>}
    {canManage && <form onSubmit={addOther} className="mt-4 grid gap-3 rounded-md border border-dashed border-slate-300 p-3 sm:grid-cols-2"><h4 className="text-sm font-semibold text-slate-900 sm:col-span-2">Ander onderdeel of eigen titel toevoegen</h4><select className="h-10 rounded-md border border-slate-200 bg-white px-3" value={form.item_type} onChange={(e) => setForm({ ...form, item_type: e.target.value, song_id: e.target.value === 'lied' ? form.song_id : '' })}>{ITEM_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select><Input className="sm:col-span-2" placeholder="Titel" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /><Input placeholder="Toonsoort" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} /><Input type="number" min="0" max="240" placeholder="Duur in minuten" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} /><Input className="sm:col-span-2" placeholder="PDF-link (optioneel, overschrijft liedkoppeling)" value={form.pdf_link_url} onChange={(e) => setForm({ ...form, pdf_link_url: e.target.value })} /><Button type="submit" size="sm" disabled={mutating} className="bg-teal-600 text-white hover:bg-teal-700 sm:col-span-2"><Plus className="mr-1 h-4 w-4" />Toevoegen</Button></form>}
  </>;
};

export default SetlistPanel;
